import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Menu,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useLocation, useSearchParams } from 'react-router-dom'
import { IconDownload, IconNotes, IconRefresh } from '@/components/icons'
import { downloadBlob } from '@/lib/download-blob'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
  PaginationBar,
  dataTableClass,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import {
  activityLogActionBadgeColor,
  formatActivityLogActionType,
  formatActivityLogEntityType,
} from '@/lib/activity-log-labels'
import { getBestScrollTop, getMainScrollElement, setMainScrollTop } from '@/lib/main-scroll-registry'
import { TICKETS_LIST_RETURN_URL_KEY } from '@/lib/open-ticket-navigation'
import { formatDateTimeDot } from '@/lib/ticket-display'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type {
  ActivityLogRow,
  ActivityLogStats,
  MyPermissionsResponse,
  ProjectRow,
  WorkspaceUserRow,
} from '@/types/api'

const ACTIVITY_LOG_PAGE_SIZE_LS = 'activity-log-page-size'
const ACTIVITY_LOG_SCROLL_BY_URL_KEY = 'sd2_activity_log_scroll_by_url_v1'
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 250, 500] as const
const ACTIVITY_LOG_PAGE_QUERY = 'page'
const ACTIVITY_LOG_PAGE_SIZE_QUERY = 'page_size'
const ACTIVITY_LOG_DAYS_QUERY = 'days'
const ACTIVITY_LOG_FROM_QUERY = 'date_time_from'
const ACTIVITY_LOG_TO_QUERY = 'date_time_to'
const ACTIVITY_LOG_PROJECTS_QUERY = 'project_ids'
const ACTIVITY_LOG_USER_QUERY = 'user_id'
const ACTIVITY_LOG_ACTIVITY_TYPE_QUERY = 'activity_type'
const ACTIVITY_LOG_ENTITY_TYPE_QUERY = 'entity_type'
const ACTIVITY_LOG_SEARCH_QUERY = 'search'
const CUSTOM_PERIOD_VALUE = 'custom'

/** Синтетические значения фильтров — совпадают с backend (activity_logs.py). */
const SYSTEM_USER_VALUE = '__system__'
const ASSIGNMENT_PRESET_VALUE = '__assigned_to__'

function toLocalDatetimeValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** datetime-local строка -> ISO для API. Возвращает undefined для пустой/невалидной строки. */
function localDatetimeToIso(value: string): string | undefined {
  if (!value) return undefined
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/** Приблизительное число дней в произвольном периоде — только для сводки /stats. */
function approxDaysBetween(fromLocal: string, toLocal: string): number {
  const from = new Date(fromLocal).getTime()
  const to = new Date(toLocal).getTime()
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 7
  return Math.max(1, Math.ceil((to - from) / (24 * 60 * 60 * 1000)))
}

type LogFiltersState = {
  projectIds: string[]
  userId: string
  activityType: string
  entityType: string
  search: string
  periodMode: 'days' | 'custom'
  days: number
  customFrom: string
  customTo: string
}

const DEFAULT_FILTERS: LogFiltersState = {
  projectIds: [],
  userId: '',
  activityType: '',
  entityType: '',
  search: '',
  periodMode: 'days',
  days: 7,
  customFrom: toLocalDatetimeValue(new Date(Date.now() - 24 * 60 * 60 * 1000)),
  customTo: toLocalDatetimeValue(new Date()),
}

function readSavedPageSize(): number {
  try {
    const raw = localStorage.getItem(ACTIVITY_LOG_PAGE_SIZE_LS)
    const n = raw ? parseInt(raw, 10) : NaN
    if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return n
  } catch {
    /* ignore */
  }
  return 50
}

function savePageSize(n: number) {
  try {
    localStorage.setItem(ACTIVITY_LOG_PAGE_SIZE_LS, String(n))
  } catch {
    /* ignore */
  }
}

function parsePositiveInt(raw: string | null, fallback: number): number {
  const n = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function parsePageSizeFromSearch(search: string): number {
  const params = new URLSearchParams(search)
  const n = parsePositiveInt(params.get(ACTIVITY_LOG_PAGE_SIZE_QUERY), readSavedPageSize())
  return PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number]) ? n : readSavedPageSize()
}

function parsePageFromSearch(search: string): number {
  const params = new URLSearchParams(search)
  return parsePositiveInt(params.get(ACTIVITY_LOG_PAGE_QUERY), 1)
}

function parseFiltersFromSearch(search: string): LogFiltersState {
  const params = new URLSearchParams(search)
  const rawProjects = params.get(ACTIVITY_LOG_PROJECTS_QUERY) || ''
  const projectIds = rawProjects
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)

  const customFrom = (params.get(ACTIVITY_LOG_FROM_QUERY) || '').trim()
  const customTo = (params.get(ACTIVITY_LOG_TO_QUERY) || '').trim()

  return {
    projectIds: Array.from(new Set(projectIds)),
    userId: (params.get(ACTIVITY_LOG_USER_QUERY) || '').trim(),
    activityType: (params.get(ACTIVITY_LOG_ACTIVITY_TYPE_QUERY) || '').trim(),
    entityType: (params.get(ACTIVITY_LOG_ENTITY_TYPE_QUERY) || '').trim(),
    search: params.get(ACTIVITY_LOG_SEARCH_QUERY) || '',
    periodMode: customFrom || customTo ? 'custom' : 'days',
    days: parsePositiveInt(params.get(ACTIVITY_LOG_DAYS_QUERY), DEFAULT_FILTERS.days),
    customFrom: customFrom || DEFAULT_FILTERS.customFrom,
    customTo: customTo || DEFAULT_FILTERS.customTo,
  }
}

function buildActivityLogSearchParams(filters: LogFiltersState, opts?: { page?: number; pageSize?: number }) {
  const params = new URLSearchParams()
  if (filters.periodMode === 'custom') {
    if (filters.customFrom) params.set(ACTIVITY_LOG_FROM_QUERY, filters.customFrom)
    if (filters.customTo) params.set(ACTIVITY_LOG_TO_QUERY, filters.customTo)
  } else if (filters.days !== DEFAULT_FILTERS.days) {
    params.set(ACTIVITY_LOG_DAYS_QUERY, String(filters.days))
  }
  if (filters.projectIds.length > 0) params.set(ACTIVITY_LOG_PROJECTS_QUERY, filters.projectIds.join(','))
  if (filters.userId) params.set(ACTIVITY_LOG_USER_QUERY, filters.userId)
  if (filters.activityType) params.set(ACTIVITY_LOG_ACTIVITY_TYPE_QUERY, filters.activityType)
  if (filters.entityType) params.set(ACTIVITY_LOG_ENTITY_TYPE_QUERY, filters.entityType)
  if (filters.search.trim()) params.set(ACTIVITY_LOG_SEARCH_QUERY, filters.search.trim())
  if ((opts?.page ?? 1) > 1) params.set(ACTIVITY_LOG_PAGE_QUERY, String(opts?.page))
  if (opts?.pageSize && opts.pageSize !== 50) params.set(ACTIVITY_LOG_PAGE_SIZE_QUERY, String(opts.pageSize))
  return params
}

function normalizeActivityLogUrlKey(pathname: string, search: string): string {
  const params = new URLSearchParams(search)
  const entries = Array.from(params.entries()).filter(([, value]) => value !== '')
  entries.sort(([a], [b]) => a.localeCompare(b))
  const normalized = new URLSearchParams()
  for (const [k, v] of entries) normalized.append(k, v)
  const s = normalized.toString()
  return s ? `${pathname}?${s}` : pathname
}

function readActivityLogScrollByUrlMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(ACTIVITY_LOG_SCROLL_BY_URL_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function writeActivityLogScrollByUrlMap(map: Record<string, number>) {
  try {
    sessionStorage.setItem(ACTIVITY_LOG_SCROLL_BY_URL_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

function persistActivityLogScroll(listUrlKey: string) {
  const el = getMainScrollElement()
  if (!el) return
  try {
    const map = readActivityLogScrollByUrlMap()
    map[listUrlKey] = el.scrollTop
    writeActivityLogScrollByUrlMap(map)
  } catch {
    /* ignore */
  }
}

function readPendingActivityLogScroll(listUrlKey: string): number | null {
  const map = readActivityLogScrollByUrlMap()
  const y = map[listUrlKey]
  return typeof y === 'number' && Number.isFinite(y) ? y : null
}

function clearPendingActivityLogScroll(listUrlKey: string) {
  const map = readActivityLogScrollByUrlMap()
  if (!(listUrlKey in map)) return
  delete map[listUrlKey]
  writeActivityLogScrollByUrlMap(map)
}

function workspaceUserLabel(u: WorkspaceUserRow): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  if (name) return name
  const un = (u.username || '').trim()
  if (un) return un
  const em = (u.email || '').trim()
  if (em) return em
  return u.id
}

export function ActivityLogPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const { openOrSelectTab } = useWorkspaceTabs()

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canLog = hasPermission(effectivePerm, 'access_activity_log')

  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [users, setUsers] = useState<WorkspaceUserRow[]>([])
  const [activityTypes, setActivityTypes] = useState<string[]>([])
  const [entityTypes, setEntityTypes] = useState<string[]>([])
  const [stats, setStats] = useState<ActivityLogStats | null>(null)

  useEffect(() => {
    if (!canLog) return
    let alive = true
    void Promise.all([
      api.projects(),
      api.workspaceUsers(),
      api.activityLogTypes(),
      api.activityLogEntities(),
    ])
      .then(([projList, userList, types, entities]) => {
        if (!alive) return
        setProjects(Array.isArray(projList) ? projList : [])
        setUsers(Array.isArray(userList) ? userList : [])
        setActivityTypes(Array.isArray(types) ? types : [])
        setEntityTypes(Array.isArray(entities) ? entities : [])
      })
      .catch(() => {
        if (!alive) return
        setProjects([])
        setUsers([])
        setActivityTypes([])
        setEntityTypes([])
      })
    return () => {
      alive = false
    }
  }, [canLog])

  const loadStats = useCallback(
    async (days: number) => {
      if (!canLog) {
        setStats(null)
        return
      }
      try {
        const s = await api.activityLogStats(days)
        setStats(s)
      } catch {
        setStats(null)
      }
    },
    [canLog],
  )

  const applied = useMemo(() => parseFiltersFromSearch(location.search), [location.search])
  const [page, setPage] = useState(() => parsePageFromSearch(location.search))
  const [pageSize, setPageSize] = useState(() => parsePageSizeFromSearch(location.search))
  const [filterDraft, setFilterDraft] = useState<LogFiltersState>(applied)

  // /stats всё ещё принимает только "N дней" — при своём периоде передаём
  // приблизительное число дней между датами (сам список фильтруется точно).
  const effectiveStatsDays = useMemo(
    () =>
      applied.periodMode === 'custom'
        ? approxDaysBetween(applied.customFrom, applied.customTo)
        : applied.days,
    [applied.periodMode, applied.customFrom, applied.customTo, applied.days],
  )

  useEffect(() => {
    setFilterDraft(applied)
  }, [applied])

  useEffect(() => {
    setPage(parsePageFromSearch(location.search))
    setPageSize(parsePageSizeFromSearch(location.search))
  }, [location.search])

  useEffect(() => {
    void loadStats(effectiveStatsDays)
  }, [effectiveStatsDays, loadStats])

  const [rows, setRows] = useState<ActivityLogRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exportingFormat, setExportingFormat] = useState<'csv' | 'json' | null>(null)

  const loadSeq = useRef(0)

  const openTicket = useCallback(
    (ticketId: string) => {
      const listUrl = normalizeActivityLogUrlKey(location.pathname, location.search)
      try {
        sessionStorage.setItem(TICKETS_LIST_RETURN_URL_KEY, listUrl)
        const map = readActivityLogScrollByUrlMap()
        map[listUrl] = getBestScrollTop()
        writeActivityLogScrollByUrlMap(map)
      } catch {
        /* ignore */
      }
      openOrSelectTab(`/tickets/${encodeURIComponent(ticketId)}`)
    },
    [location.pathname, location.search, openOrSelectTab],
  )

  const load = useCallback(async () => {
    if (!canLog) {
      setRows([])
      setTotal(0)
      setLoading(false)
      return
    }
    const seq = ++loadSeq.current
    setLoading(true)
    setError(null)
    try {
      const offset = (page - 1) * pageSize
      const params: Parameters<typeof api.activityLogs>[0] = {
        limit: pageSize,
        offset,
      }
      if (applied.periodMode === 'custom') {
        params.date_time_from = localDatetimeToIso(applied.customFrom)
        params.date_time_to = localDatetimeToIso(applied.customTo)
      } else {
        params.days = applied.days
      }
      if (applied.projectIds.length) params.project_id = applied.projectIds.join(',')
      if (applied.userId) params.user_id = applied.userId
      if (applied.activityType) params.activity_type = applied.activityType
      if (applied.entityType) params.entity_type = applied.entityType
      if (applied.search.trim()) params.search = applied.search.trim()

      const res = await api.activityLogs(params)
      if (seq !== loadSeq.current) return
      const items = Array.isArray(res.items) ? res.items : []
      const seen = new Set<string>()
      const deduped = items.filter((r) => {
        if (!r?.id || seen.has(r.id)) return false
        seen.add(r.id)
        return true
      })
      setRows(deduped)
      setTotal(typeof res.total_count === 'number' && Number.isFinite(res.total_count) ? res.total_count : deduped.length)
    } catch (e) {
      if (seq !== loadSeq.current) return
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить лог')
      setRows([])
      setTotal(0)
    } finally {
      if (seq === loadSeq.current) setLoading(false)
    }
  }, [
    applied.activityType,
    applied.days,
    applied.periodMode,
    applied.customFrom,
    applied.customTo,
    applied.entityType,
    applied.projectIds,
    applied.search,
    applied.userId,
    canLog,
    page,
    pageSize,
  ])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const listUrlKey = normalizeActivityLogUrlKey(location.pathname, location.search)
    let debounceTimer = 0
    let detach: (() => void) | undefined
    let retryId: number | undefined

    const onScroll = () => {
      window.clearTimeout(debounceTimer)
      debounceTimer = window.setTimeout(() => persistActivityLogScroll(listUrlKey), 120)
    }

    const setup = (target: HTMLElement) => {
      target.addEventListener('scroll', onScroll, { passive: true })
      detach = () => {
        window.clearTimeout(debounceTimer)
        target.removeEventListener('scroll', onScroll)
      }
    }

    const el = getMainScrollElement()
    if (el) {
      setup(el)
    } else {
      retryId = window.setTimeout(() => {
        const next = getMainScrollElement()
        if (next) setup(next)
      }, 0)
    }

    return () => {
      if (retryId != null) window.clearTimeout(retryId)
      detach?.()
    }
  }, [location.pathname, location.search])

  useEffect(() => {
    if (loading) return
    const listUrlKey = normalizeActivityLogUrlKey(location.pathname, location.search)
    const y = readPendingActivityLogScroll(listUrlKey)
    if (y == null) return

    let cancelled = false
    const tryApply = () => {
      if (cancelled) return
      setMainScrollTop(y)
      const el = getMainScrollElement()
      if (!el) return
      const max = Math.max(0, el.scrollHeight - el.clientHeight)
      if (y > 0 && max < 8) return
      const target = Math.min(y, max)
      if (el.scrollTop >= target - 4) clearPendingActivityLogScroll(listUrlKey)
    }

    tryApply()
    const timeouts = [0, 24, 72, 160, 320, 600].map((ms) => window.setTimeout(tryApply, ms))
    const done = window.setTimeout(() => {
      if (!cancelled) clearPendingActivityLogScroll(listUrlKey)
    }, 900)
    const el = getMainScrollElement()
    const ro =
      el && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            tryApply()
          })
        : null
    if (el && ro) ro.observe(el)

    return () => {
      cancelled = true
      timeouts.forEach((id) => window.clearTimeout(id))
      window.clearTimeout(done)
      ro?.disconnect()
    }
  }, [loading, location.pathname, location.search, rows.length])

  const applyFilters = useCallback(() => {
    const nextFilters = { ...filterDraft }
    setSearchParams(buildActivityLogSearchParams(nextFilters, { page: 1, pageSize }), { replace: true })
  }, [filterDraft, pageSize, setSearchParams])

  const resetFilters = useCallback(() => {
    setFilterDraft(DEFAULT_FILTERS)
    setSearchParams(buildActivityLogSearchParams(DEFAULT_FILTERS, { page: 1, pageSize }), { replace: true })
  }, [pageSize, setSearchParams])

  const onRefresh = useCallback(() => {
    void loadStats(effectiveStatsDays)
    void load()
  }, [effectiveStatsDays, load, loadStats])

  const onExport = useCallback(
    async (format: 'csv' | 'json') => {
      setExportingFormat(format)
      try {
        const periodParams =
          applied.periodMode === 'custom'
            ? {
                date_time_from: localDatetimeToIso(applied.customFrom),
                date_time_to: localDatetimeToIso(applied.customTo),
              }
            : { days: applied.days }
        const blob = await api.exportActivityLogs({
          ...periodParams,
          project_id: applied.projectIds.length ? applied.projectIds.join(',') : undefined,
          user_id: applied.userId || undefined,
          activity_type: applied.activityType || undefined,
          entity_type: applied.entityType || undefined,
          search: applied.search.trim() || undefined,
          format,
        })
        const stamp = applied.periodMode === 'custom' ? 'custom-period' : `days-${applied.days}`
        downloadBlob(blob, `activity-log-${stamp}.${format}`)
      } catch (e) {
        showNotification({
          title: 'Выгрузка',
          message: e instanceof ApiError ? e.message : 'Не удалось выгрузить лог действий',
          color: 'red',
        })
      } finally {
        setExportingFormat(null)
      }
    },
    [
      applied.activityType,
      applied.days,
      applied.periodMode,
      applied.customFrom,
      applied.customTo,
      applied.entityType,
      applied.projectIds,
      applied.search,
      applied.userId,
    ],
  )

  const onPageSizeChange = useCallback((v: string | null) => {
    const n = parseInt(String(v), 10)
    if (!PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return
    setPageSize(n)
    setPage(1)
    savePageSize(n)
    setSearchParams(buildActivityLogSearchParams(applied, { page: 1, pageSize: n }), { replace: true })
  }, [applied, setSearchParams])

  const onPageChange = useCallback(
    (nextPage: number) => {
      setPage(nextPage)
      const next = new URLSearchParams(searchParams)
      if (nextPage <= 1) next.delete(ACTIVITY_LOG_PAGE_QUERY)
      else next.set(ACTIVITY_LOG_PAGE_QUERY, String(nextPage))
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const pages = Math.max(1, Math.ceil(total / pageSize))

  const projectSelectData = useMemo(() => {
    const opts = [{ value: '', label: 'Все проекты' }]
    for (const p of projects) {
      opts.push({ value: p.id, label: p.name || p.slug || p.id })
    }
    return opts
  }, [projects])

  const userSelectData = useMemo(() => {
    const opts = [
      { value: '', label: 'Все пользователи' },
      { value: SYSTEM_USER_VALUE, label: 'Система' },
    ]
    const sorted = [...users].sort((a, b) =>
      workspaceUserLabel(a).localeCompare(workspaceUserLabel(b), 'ru'),
    )
    for (const u of sorted) {
      opts.push({ value: u.id, label: workspaceUserLabel(u) })
    }
    return opts
  }, [users])

  const activityTypeData = useMemo(() => {
    const opts = [
      { value: '', label: 'Все действия' },
      { value: ASSIGNMENT_PRESET_VALUE, label: 'Назначения на сотрудника (авто + вручную)' },
    ]
    const sorted = [...activityTypes].sort((a, b) =>
      formatActivityLogActionType(a).localeCompare(formatActivityLogActionType(b), 'ru'),
    )
    for (const t of sorted) {
      opts.push({ value: t, label: formatActivityLogActionType(t) })
    }
    return opts
  }, [activityTypes])

  const entityTypeData = useMemo(() => {
    const opts = [{ value: '', label: 'Все объекты' }]
    for (const t of entityTypes) {
      opts.push({ value: t, label: formatActivityLogEntityType(t) })
    }
    return opts
  }, [entityTypes])

  const topTypes = useMemo(() => {
    if (!stats?.activities_by_type) return []
    return Object.entries(stats.activities_by_type)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
  }, [stats])

  if (!canLog) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для раздела «Лог действий».
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconNotes size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Лог действий
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                История действий пользователей и системы с фильтрами по периоду, проектам и типам событий.
              </Text>
            </Box>
          }
        />

        <Card withBorder padding="sm" radius="md">
          <Group gap="md" wrap="wrap" align="center">
            <Group gap={6} wrap="nowrap">
              <Text size="xs" c="dimmed">
                Всего за период:
              </Text>
              <Text size="sm" fw={600}>
                {stats?.total_activities ?? '—'}
              </Text>
            </Group>
            {topTypes.map(([type, count]) => (
              <Badge key={type} variant="light" color="gray" size="sm">
                {formatActivityLogActionType(type)}: {count}
              </Badge>
            ))}
          </Group>
        </Card>

        <PageToolbar
          leading={
            <Group gap="sm" wrap="wrap" align="flex-end">
              <Select
                size="xs"
                label="Период"
                data={[
                  { value: '1', label: 'Сегодня' },
                  { value: '7', label: '7 дней' },
                  { value: '14', label: '14 дней' },
                  { value: '30', label: '30 дней' },
                  { value: '90', label: '90 дней' },
                  { value: CUSTOM_PERIOD_VALUE, label: 'Свой период' },
                ]}
                value={filterDraft.periodMode === 'custom' ? CUSTOM_PERIOD_VALUE : String(filterDraft.days)}
                onChange={(value) => {
                  const v = value || '7'
                  if (v === CUSTOM_PERIOD_VALUE) {
                    setFilterDraft((f) => ({ ...f, periodMode: 'custom' }))
                  } else {
                    setFilterDraft((f) => ({ ...f, periodMode: 'days', days: parseInt(v, 10) || 7 }))
                  }
                }}
                allowDeselect={false}
                checkIconPosition="right"
                styles={{ root: { width: 130 } }}
              />
              {filterDraft.periodMode === 'custom' ? (
                <>
                  <TextInput
                    type="datetime-local"
                    aria-label="От (дата и время)"
                    value={filterDraft.customFrom}
                    onChange={(e) => setFilterDraft((f) => ({ ...f, customFrom: e.currentTarget.value }))}
                    size="xs"
                    styles={{ root: { width: 180, alignSelf: 'flex-end' } }}
                  />
                  <TextInput
                    type="datetime-local"
                    aria-label="До (дата и время)"
                    value={filterDraft.customTo}
                    onChange={(e) => setFilterDraft((f) => ({ ...f, customTo: e.currentTarget.value }))}
                    size="xs"
                    styles={{ root: { width: 180, alignSelf: 'flex-end' } }}
                  />
                </>
              ) : null}
              <MultiSelect
                size="xs"
                label="Проект"
                data={projectSelectData.filter((x) => x.value)}
                value={filterDraft.projectIds}
                onChange={(vals) => setFilterDraft((f) => ({ ...f, projectIds: vals }))}
                maw={260}
                searchable
                clearable
                placeholder="Все проекты"
                nothingFoundMessage="Нет"
              />
              <Select
                size="xs"
                label="Пользователь"
                data={userSelectData}
                value={filterDraft.userId || ''}
                onChange={(v) => setFilterDraft((f) => ({ ...f, userId: v || '' }))}
                searchable
                nothingFoundMessage="Не найдено"
                maw={220}
              />
              <Select
                size="xs"
                label="Действие"
                data={activityTypeData}
                value={filterDraft.activityType || ''}
                onChange={(v) => setFilterDraft((f) => ({ ...f, activityType: v || '' }))}
                searchable
                nothingFoundMessage="Не найдено"
                maw={260}
              />
              <Select
                size="xs"
                label="Объект"
                data={entityTypeData}
                value={filterDraft.entityType}
                onChange={(value) => setFilterDraft((f) => ({ ...f, entityType: value || '' }))}
                searchable
                allowDeselect={false}
                checkIconPosition="right"
                styles={{ root: { width: 190 } }}
              />
              <TextInput
                size="xs"
                label="Поиск"
                placeholder="По описанию…"
                value={filterDraft.search}
                onChange={(e) => setFilterDraft((f) => ({ ...f, search: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters()
                }}
                maw={220}
              />
              <Button
                size="xs"
                onClick={applyFilters}
                loading={loading}
                loaderProps={{ type: 'oval', size: 14 }}
              >
                Применить
              </Button>
              <Button size="xs" variant="default" onClick={resetFilters}>
                Сбросить
              </Button>
            </Group>
          }
          trailing={
            <Group gap="xs">
              <Menu shadow="md" width={180} position="bottom-end">
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size="md"
                    aria-label="Выгрузить лог"
                    title="Выгрузить лог"
                    disabled={exportingFormat !== null}
                  >
                    <IconDownload size={16} stroke={1.5} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => void onExport('csv')}>Скачать CSV</Menu.Item>
                  <Menu.Item onClick={() => void onExport('json')}>Скачать JSON</Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <ActionIcon variant="default" size="md" aria-label="Обновить" title="Обновить" onClick={onRefresh}>
                <IconRefresh size={16} stroke={1.5} />
              </ActionIcon>
            </Group>
          }
        />

        {error ? (
          <Alert color="red" variant="light" title="Ошибка загрузки">
            {error}
          </Alert>
        ) : null}

        {loading && rows.length === 0 ? (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <PageCenterLoader label="Загрузка лога" minHeight={160} />
          </Box>
        ) : !loading && rows.length === 0 ? (
          <EmptyState
            title="Нет записей"
            description="За выбранный период и фильтры событий нет."
            icon={<IconNotes size={28} stroke={1.5} />}
          />
        ) : (
          <Card withBorder padding="sm" radius="md" pos="relative" aria-busy={loading}>
            {loading ? (
              <Box
                pos="absolute"
                inset={0}
                style={{
                  zIndex: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'color-mix(in srgb, var(--mantine-color-body) 72%, transparent)',
                  borderRadius: 'var(--mantine-radius-md)',
                }}
              >
                <Stack align="center" gap="xs" py="md">
                  <Loader size="md" type="oval" />
                  <Text size="sm" c="dimmed">
                    Загрузка…
                  </Text>
                </Stack>
              </Box>
            ) : null}
            <Stack gap="sm">
              {total > 0 ? (
                <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                  <Group gap="xs" wrap="wrap" align="center">
                    <Text size="xs" c="dimmed">
                      Показывать:
                    </Text>
                    <Select
                      size="xs"
                      data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                      value={String(pageSize)}
                      onChange={(value) => onPageSizeChange(value || String(PAGE_SIZE_OPTIONS[0]))}
                      allowDeselect={false}
                      checkIconPosition="right"
                      styles={{ root: { width: 80 } }}
                    />
                    <Text size="xs" c="dimmed">
                      записей
                    </Text>
                  </Group>
                  <Group gap="md" wrap="wrap" align="center">
                    <Text size="xs" c="dimmed">
                      Всего:{' '}
                      <Text component="span" fw={600}>
                        {total}
                      </Text>
                    </Text>
                    {pages > 1 ? (
                      <PaginationBar
                        page={page}
                        pageCount={pages}
                        total={total}
                        disabled={loading}
                        onPageChange={onPageChange}
                      />
                    ) : null}
                  </Group>
                </Group>
              ) : null}

              <DataTableScroll>
                <DataTable className={dataTableClass.table}>
                  <DataThead>
                    <DataTr>
                      <DataTh style={{ width: 150 }}>Дата и время</DataTh>
                      <DataTh style={{ width: 160 }}>Пользователь</DataTh>
                      <DataTh style={{ width: 180 }}>Действие</DataTh>
                      <DataTh>Описание</DataTh>
                      <DataTh style={{ width: 120 }}>Проект</DataTh>
                      <DataTh style={{ width: 88 }}>Тикет</DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {rows.map((row) => {
                      const clickable = !!row.ticket_id
                      return (
                        <DataTr
                          key={row.id}
                          style={{ cursor: clickable ? 'pointer' : undefined }}
                          onClick={() => {
                            if (row.ticket_id) openTicket(row.ticket_id)
                          }}
                        >
                          <DataTd>
                            <Text size="xs" c="dimmed">
                              {formatDateTimeDot(row.created_at)}
                            </Text>
                          </DataTd>
                          <DataTd>
                            <Text size="sm">{row.user_name || 'Система'}</Text>
                          </DataTd>
                          <DataTd>
                            <Badge size="sm" variant="light" color={activityLogActionBadgeColor(row.activity_type)}>
                              {formatActivityLogActionType(row.activity_type)}
                            </Badge>
                          </DataTd>
                          <DataTd>
                            <Text size="sm">{row.description}</Text>
                          </DataTd>
                          <DataTd>
                            <Text size="xs" c="dimmed">
                              {row.project_name || '—'}
                            </Text>
                          </DataTd>
                          <DataTd>
                            {row.ticket_number != null ? (
                              <Text size="xs" ff="monospace" c="blue">
                                #{row.ticket_number}
                              </Text>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </DataTd>
                        </DataTr>
                      )
                    })}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>
            </Stack>
          </Card>
        )}
      </Stack>
    </PageShell>
  )
}
